// Плановая сумма по категории на месяц. Создание и правка — одна форма:
// разница только в том, можно ли менять категорию.

import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { BudgetCategoryRow, Category } from '../api/types';
import { formatMinor, parseAmountToMinor } from '../lib/money';
import { haptic } from '../lib/telegram';
import { humanError } from '../lib/errors';
import { useToast } from '../lib/toast';
import { SheetCta } from './SheetCta';
import { useBackButton, useMainButton } from '../lib/use-main-button';
import { useViewCurrency } from '../lib/view-currency';

type Props = {
  period: string;
  initial: BudgetCategoryRow | null;
  categories: Category[];
  usedCategoryIds: string[];
  onClose: () => void;
  onSaved: () => void;
};

export function BudgetPlanSheet({
  period,
  initial,
  categories,
  usedCategoryIds,
  onClose,
  onSaved,
}: Props): JSX.Element {
  const isEdit = initial != null;
  const [categoryId, setCategoryId] = useState(initial?.category_id ?? '');
  // formatMinor, а не деление на 100n: целочисленное деление срезало копейки,
  // и правка плана 1 500 000,50 молча сохраняла 1 500 000,00.
  const [amountMajor, setAmountMajor] = useState(
    initial ? formatMinor(initial.planned_minor, 'UZS') : '',
  );
  const [note, setNote] = useState(initial?.note ?? '');

  const { formatShortMoney } = useViewCurrency();

  useBackButton(onClose);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Подсказка «сколько обычно уходит»: средний факт за 3 месяца и план прошлого
  // месяца. Цифра из головы почти всегда хуже цифры из истории.
  const suggestQ = useQuery({
    queryKey: ['budget-suggest', period],
    queryFn: () => api.budgetSuggest(period),
  });
  const suggestion = suggestQ.data?.suggestions.find((s) => s.category_id === categoryId);

  const toast = useToast();
  const saveMut = useMutation({
    mutationFn: (minor: bigint) =>
      api.upsertBudgetPlan({
        period,
        category_id: categoryId,
        planned_amount_minor: minor.toString(),
        note: note.trim() || null,
      }),
    onSuccess: () => {
      haptic('success');
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(humanError(e, 'Не удалось сохранить план')),
  });

  const minor = parseAmountToMinor(amountMajor);
  const isValid = !!categoryId && minor !== null && minor > 0n;

  const cta = useMainButton({
    text: isEdit ? 'Сохранить' : 'Добавить в бюджет',
    onClick: () => {
      if (!isValid || minor === null) return;
      saveMut.mutate(minor);
    },
    enabled: isValid && !saveMut.isPending,
    progress: saveMut.isPending,
  });

  const taken = new Set(usedCategoryIds);
  const available = isEdit ? categories : categories.filter((c) => !taken.has(c.id));

  const applySuggestion = (minorStr: string): void => {
    haptic('light');
    setAmountMajor((BigInt(minorStr) / 100n).toString());
  };

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div className="sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="sheet-handle" />
        <div className="sheet-title">{isEdit ? 'Изменить план' : 'План по категории'}</div>

        <label className="form-field">
          <span className="form-label">Категория</span>
          {isEdit ? (
            <input
              type="text"
              className="form-input"
              value={initial?.category_name ?? ''}
              disabled
            />
          ) : (
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="form-select"
            >
              <option value="">Выберите категорию</option>
              {available.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.kind === 'income' ? ' (доход)' : ''}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="form-field">
          <span className="form-label">Сумма на месяц (UZS)</span>
          <input
            type="text"
            inputMode="decimal"
            className={`form-input ${!isValid && amountMajor ? 'invalid' : ''}`}
            value={amountMajor}
            onChange={(e) => setAmountMajor(e.target.value)}
            placeholder="500 000"
            autoFocus={!isEdit}
          />
        </label>

        {suggestion && (suggestion.avg_3m_minor !== '0' || suggestion.prev_plan_minor !== null) ? (
          <div className="suggest-row">
            {suggestion.avg_3m_minor !== '0' ? (
              <button
                type="button"
                className="suggest-chip"
                onClick={() => applySuggestion(suggestion.avg_3m_minor)}
              >
                в среднем {formatShortMoney(suggestion.avg_3m_minor)}
              </button>
            ) : null}
            {suggestion.prev_plan_minor ? (
              <button
                type="button"
                className="suggest-chip"
                onClick={() => applySuggestion(suggestion.prev_plan_minor as string)}
              >
                в прошлом месяце {formatShortMoney(suggestion.prev_plan_minor)}
              </button>
            ) : null}
          </div>
        ) : null}

        <label className="form-field">
          <span className="form-label">Заметка</span>
          <input
            type="text"
            className="form-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
            placeholder="Необязательно"
          />
        </label>

        <div className="sheet-actions">
          <SheetCta {...cta} />
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saveMut.isPending}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
